import camelCase from 'lodash/camelCase';
import merge from 'lodash/merge';
import { generateRadixColors } from 'radix-theme-generator';

import constants from '../../designTokens/build/constants';
import runtimeConstants from '../../designTokens/build/runtimeConstants';

import type tokens from '../../designTokens/build/constants.d.ts';

export type Tokens = typeof tokens;

// The actual function that populates runtime tokens
const populateReference = (string, _mode, generatedPalette) => {
  // remove parens and split into segments
  const segments = string.replaceAll('(', '').replaceAll(')', '').split('.');
  const color = camelCase(segments[2]);
  const mode = segments[3];

  // Its already been transformed
  if (segments.length === 1 && string.startsWith('#')) {
    return string;
  }

  const result = generatedPalette[color][mode][segments[4]];

  return result;
};

// Recursively walk the runtime tokens, looking for $value fields to populate
const populateValues = (runtimeConstants, generatedPalette) => {
  let objToReturn = {} as any;
  if (runtimeConstants.$value) {
    if (typeof runtimeConstants.$value === 'string') {
      objToReturn = populateReference(
        runtimeConstants.$value,
        'light',
        generatedPalette
      );
    }
    if (runtimeConstants.$value?.dark && runtimeConstants.$value?.light) {
      objToReturn = {
        dark: populateReference(
          runtimeConstants.$value.dark,
          'dark',
          generatedPalette
        ),
        light: populateReference(
          runtimeConstants.$value.light,
          'light',
          generatedPalette
        ),
      };
    }
  }
  if (!runtimeConstants.$value) {
    for (const [key, value] of Object.entries(runtimeConstants)) {
      if (value && typeof value === 'object') {
        objToReturn[key] = populateValues(value, generatedPalette);
      }
    }
  }
  return objToReturn;
};

export const initializeTokens = (basePrimaryColor: string) => {
  // Generate light theme colors
  const lightThemeColors = generateRadixColors({
    appearance: 'light',
    accent: basePrimaryColor,
    gray: constants.theme.color['grayLight'] as unknown as string,
    background: constants.theme.color['backgroundLight'] as unknown as string,
  });

  // Generate dark theme colors
  const darkThemeColors = generateRadixColors({
    appearance: 'dark',
    accent: basePrimaryColor,
    gray: constants.theme.color['grayDark'] as unknown as string,
    background: constants.theme.color['backgroundDark'] as unknown as string,
  });

  const mappedColors = {
    accent: {
      light: {
        1: lightThemeColors.accentScale[0],
        2: lightThemeColors.accentScale[1],
        3: lightThemeColors.accentScale[2],
        4: lightThemeColors.accentScale[3],
        5: lightThemeColors.accentScale[4],
        6: lightThemeColors.accentScale[5],
        7: lightThemeColors.accentScale[6],
        8: lightThemeColors.accentScale[7],
        9: lightThemeColors.accentScale[8],
        10: lightThemeColors.accentScale[9],
        11: lightThemeColors.accentScale[10],
        12: lightThemeColors.accentScale[11],
        a1: lightThemeColors.accentScaleAlpha[0],
        a2: lightThemeColors.accentScaleAlpha[1],
        a3: lightThemeColors.accentScaleAlpha[2],
        a4: lightThemeColors.accentScaleAlpha[3],
        a5: lightThemeColors.accentScaleAlpha[4],
        a6: lightThemeColors.accentScaleAlpha[5],
        a7: lightThemeColors.accentScaleAlpha[6],
        a8: lightThemeColors.accentScaleAlpha[7],
        a9: lightThemeColors.accentScaleAlpha[8],
        a10: lightThemeColors.accentScaleAlpha[9],
        a11: lightThemeColors.accentScaleAlpha[10],
        a12: lightThemeColors.accentScaleAlpha[11],
        surface: lightThemeColors.accentSurface,
        contrast: lightThemeColors.accentContrast,
        background: lightThemeColors.background,
        // These 2 will be deleted
        indicator: lightThemeColors.accentScale[9],
        track: lightThemeColors.accentScale[9],
      },
      dark: {
        1: darkThemeColors.accentScale[0],
        2: darkThemeColors.accentScale[1],
        3: darkThemeColors.accentScale[2],
        4: darkThemeColors.accentScale[3],
        5: darkThemeColors.accentScale[4],
        6: darkThemeColors.accentScale[5],
        7: darkThemeColors.accentScale[6],
        8: darkThemeColors.accentScale[7],
        9: darkThemeColors.accentScale[8],
        10: darkThemeColors.accentScale[9],
        11: darkThemeColors.accentScale[10],
        12: darkThemeColors.accentScale[11],
        a1: darkThemeColors.accentScaleAlpha[0],
        a2: darkThemeColors.accentScaleAlpha[1],
        a3: darkThemeColors.accentScaleAlpha[2],
        a4: darkThemeColors.accentScaleAlpha[3],
        a5: darkThemeColors.accentScaleAlpha[4],
        a6: darkThemeColors.accentScaleAlpha[5],
        a7: darkThemeColors.accentScaleAlpha[6],
        a8: darkThemeColors.accentScaleAlpha[7],
        a9: darkThemeColors.accentScaleAlpha[8],
        a10: darkThemeColors.accentScaleAlpha[9],
        a11: darkThemeColors.accentScaleAlpha[10],
        a12: darkThemeColors.accentScaleAlpha[11],
        surface: darkThemeColors.accentSurface,
        contrast: darkThemeColors.accentContrast,
        background: darkThemeColors.background,
        // These 2 will be deleted
        indicator: darkThemeColors.accentScale[9],
        track: darkThemeColors.accentScale[9],
      },
    },
    gray: {
      light: {
        1: lightThemeColors.grayScale[0],
        2: lightThemeColors.grayScale[1],
        3: lightThemeColors.grayScale[2],
        4: lightThemeColors.grayScale[3],
        5: lightThemeColors.grayScale[4],
        6: lightThemeColors.grayScale[5],
        7: lightThemeColors.grayScale[6],
        8: lightThemeColors.grayScale[7],
        9: lightThemeColors.grayScale[8],
        10: lightThemeColors.grayScale[9],
        11: lightThemeColors.grayScale[10],
        12: lightThemeColors.grayScale[11],
        a1: lightThemeColors.grayScaleAlpha[0],
        a2: lightThemeColors.grayScaleAlpha[1],
        a3: lightThemeColors.grayScaleAlpha[2],
        a4: lightThemeColors.grayScaleAlpha[3],
        a5: lightThemeColors.grayScaleAlpha[4],
        a6: lightThemeColors.grayScaleAlpha[5],
        a7: lightThemeColors.grayScaleAlpha[6],
        a8: lightThemeColors.grayScaleAlpha[7],
        a9: lightThemeColors.grayScaleAlpha[8],
        a10: lightThemeColors.grayScaleAlpha[9],
        a11: lightThemeColors.grayScaleAlpha[10],
        a12: lightThemeColors.grayScaleAlpha[11],
        surface: lightThemeColors.graySurface,
        background: lightThemeColors.background,
        // These 2 will be deleted
        indicator: lightThemeColors.grayScale[9],
        track: lightThemeColors.grayScale[9],
      },
      dark: {
        1: darkThemeColors.grayScale[0],
        2: darkThemeColors.grayScale[1],
        3: darkThemeColors.grayScale[2],
        4: darkThemeColors.grayScale[3],
        5: darkThemeColors.grayScale[4],
        6: darkThemeColors.grayScale[5],
        7: darkThemeColors.grayScale[6],
        8: darkThemeColors.grayScale[7],
        9: darkThemeColors.grayScale[8],
        10: darkThemeColors.grayScale[9],
        11: darkThemeColors.grayScale[10],
        12: darkThemeColors.grayScale[11],
        a1: darkThemeColors.grayScaleAlpha[0],
        a2: darkThemeColors.grayScaleAlpha[1],
        a3: darkThemeColors.grayScaleAlpha[2],
        a4: darkThemeColors.grayScaleAlpha[3],
        a5: darkThemeColors.grayScaleAlpha[4],
        a6: darkThemeColors.grayScaleAlpha[5],
        a7: darkThemeColors.grayScaleAlpha[6],
        a8: darkThemeColors.grayScaleAlpha[7],
        a9: darkThemeColors.grayScaleAlpha[8],
        a10: darkThemeColors.grayScaleAlpha[9],
        a11: darkThemeColors.grayScaleAlpha[10],
        a12: darkThemeColors.grayScaleAlpha[11],
        surface: darkThemeColors.graySurface,
        background: darkThemeColors.background,
        // These 2 will be deleted
        indicator: darkThemeColors.grayScale[9],
        track: darkThemeColors.grayScale[9],
      },
    },
  };

  const populated = populateValues(runtimeConstants, mappedColors);

  return merge({}, constants, populated) as Tokens;
};
